/**
 * MeshActionPanel — side panel triggered when a mesh node is clicked in the DAG.
 *
 * Shows mesh info and offers wing area detection. On confirmation, saves
 * the detected area as ref_area via setReferenceQuantity.
 */

import { useState } from "react";
import type { DAGNode } from "../../api/artifacts";
import { setReferenceQuantity } from "../../api/projects";
import {
  detectWingArea,
  confirmWingArea,
  type WingAreaDetectionResult,
} from "../../flow360/api";
import { usePlanformStore } from "../../stores/planformStore";
import { useProvenanceContext } from "../../context/ProvenanceContext";

interface Props {
  node: DAGNode;
  projectId: string;
  onClose: () => void;
}

function resolveSurfaceMeshId(node: DAGNode): string {
  const meta = node.metadata ?? {};
  if (node.kind === "surface_mesh") return meta.flow360_surface_mesh_id ?? "";
  if (node.kind === "volume_mesh") return meta.flow360_surface_mesh_id ?? "";
  return "";
}

export function MeshActionPanel({ node, projectId, onClose }: Props) {
  const [detecting, setDetecting] = useState(false);
  const [result, setResult] = useState<WingAreaDetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setOpenPanel } = useProvenanceContext();

  const surfaceMeshId = resolveSurfaceMeshId(node);

  const handleDetect = async () => {
    if (!surfaceMeshId) return;
    setDetecting(true);
    setResult(null);
    setError(null);
    try {
      const res = await detectWingArea(surfaceMeshId, projectId, { includeViz: true });
      setResult(res);
      if (res.viz) {
        const v = res.viz;
        usePlanformStore.getState().setData({
          vizData: v,
          liftAxisIdx: v.lift_axis_idx,
          planformAxes: v.planform_axes,
          projZ: v.trapezoid_outline[0]?.[v.lift_axis_idx] ?? 0,
        });
        setOpenPanel("viewer");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetecting(false);
    }
  };

  const handleUseArea = async (area: number) => {
    if (result?.detection_id) {
      try { await confirmWingArea(result.detection_id, area, projectId); } catch { /* best-effort */ }
    }
    const lu: string = result?.length_unit ?? "m";
    try {
      await setReferenceQuantity(projectId, "ref_area", area, `${lu}²`, "wing_area_detection");
    } catch { /* best-effort */ }
    setResult(null);
    onClose();
  };

  return (
    <div style={{
      position: "absolute",
      top: 10,
      left: 10,
      zIndex: 10,
      background: "var(--color-surface)",
      border: "1px solid var(--gray-a5)",
      borderRadius: 8,
      padding: 16,
      width: 280,
      boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
      fontSize: 13,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontWeight: 600 }}>{node.kind.replace("_", " ")}</span>
        <button
          onClick={onClose}
          style={{ background: "transparent", border: "none", color: "var(--gray-9)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
        >&times;</button>
      </div>

      <div style={{ marginBottom: 8, color: "var(--gray-11)" }}>
        <div><strong>Name:</strong> {node.name}</div>
        <div style={{ fontSize: 11, color: "var(--gray-9)", marginTop: 2 }}>{node.path}</div>
      </div>

      {!surfaceMeshId && (
        <div style={{ color: "var(--yellow-9)", fontSize: 12, marginBottom: 8 }}>
          No surface mesh ID in metadata. Cannot run detection.
        </div>
      )}

      {error && (
        <div style={{ color: "var(--red-9)", fontSize: 12, marginBottom: 8 }}>
          Detection failed: {error}
        </div>
      )}

      {result && (
        <div style={{ marginBottom: 8 }}>
          <div>S_gross: <strong>{result.s_gross?.toFixed(4) ?? "—"}</strong></div>
          <div>S_net: <strong>{result.s_net?.toFixed(4) ?? "—"}</strong></div>
          <div style={{ fontSize: 11, color: "var(--gray-9)", marginTop: 4 }}>
            {result.cached
              ? `cached result, ${result.confidence}`
              : `${result.n_faces_selected}/${result.n_faces_total} faces, ${result.confidence}`}
            {result.viz && " — toggle Planform in the 3D viewer to inspect"}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button
              onClick={() => handleUseArea(result.s_gross ?? result.projected_area)}
              style={{
                padding: "4px 12px", borderRadius: 4, border: "none",
                background: "var(--green-9)", color: "white", fontSize: 12, cursor: "pointer",
              }}
            >Use as S_ref</button>
            <button
              onClick={() => setResult(null)}
              style={{
                padding: "4px 12px", borderRadius: 4, border: "1px solid var(--gray-a6)",
                background: "transparent", color: "var(--gray-11)", fontSize: 12, cursor: "pointer",
              }}
            >Dismiss</button>
          </div>
        </div>
      )}

      {!result && (
        <button
          onClick={handleDetect}
          disabled={detecting || !surfaceMeshId}
          style={{
            width: "100%",
            padding: "6px 12px",
            borderRadius: 6,
            border: "none",
            background: surfaceMeshId ? "var(--blue-9)" : "var(--gray-6)",
            color: "white",
            fontSize: 13,
            cursor: surfaceMeshId ? "pointer" : "not-allowed",
            opacity: detecting ? 0.6 : 1,
          }}
        >
          {detecting ? "Detecting..." : "Detect Wing Area"}
        </button>
      )}
    </div>
  );
}
